import Vue from 'vue';
import Component from 'vue-class-component';
import {DownloadHelper} from '../../helpers/download';
import {getWhitelistedParentUrl} from '../../helpers/environment';
import {Watch} from 'vue-property-decorator';
import {DataManager, Visualisation, Toolbar, Chart, FilterBar, CustomDynamicView} from 'wc-visualisation';
import {Table, Icon, Popover, pivotData, formatDate, FieldType} from 'wc-lib';
import {format} from 'date-fns';

require('wc-lib/icons/gas.svg');
require('wc-lib/icons/arrowright.svg');

@Component({
  template: require('./gas-dashboard.html'),
  name: 'gas-dashboard',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'vis-table': Table,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class GasDashboard extends Vue {
  data: Array<any> = [];
  dynamicView: CustomDynamicView<any>;
  selectedRegion: string = 'Victoria';
  disabledRegions = [];
  currentInterval: any = {};
  nextInterval: any = {};
  id: string;
  gshLocations = [];
  selectedGshLocation: string = null;
  dwgmChartConfig: any = null;
  sttmChartConfig: any = null;
  gshChartConfig: any = null;
  guideColour = '#777da7';
  intervalRefresh: any;
  parentUrl = getWhitelistedParentUrl();
  dwgmDashboardUrl = this.parentUrl + '/Energy-systems/Gas/Declared-Wholesale-Gas-Market-DWGM/Data-DWGM/Data-dashboard';
  sttmDashboardUrl = this.parentUrl + '/Energy-systems/Gas/Short-Term-Trading-Market-STTM/Data-STTM/Data-dashboard-STTM';
  gshDashboardUrl = this.parentUrl + '/Energy-systems/Gas/Gas-Supply-Hub-GSH/Data-GSH/Data-dashboard';

  get isSttm() {
    return this.selectedRegion === 'Sydney' || this.selectedRegion === 'Brisbane' || this.selectedRegion === 'Adelaide';
  }

  get isDwgm() {
    return this.selectedRegion === 'Victoria';
  }

  get isGsh() {
    return this.selectedRegion === 'GSH';
  }

  get priceTitle() {
    if (this.isGsh) {
      return 'VOL WEIGHTED PRICE';
    } else {
      return 'PRICE';
    }
  }

  get demandTitle() {
    if (this.isGsh) {
      return 'QUANTITY';
    } else {
      return 'DEMAND';
    }
  }

  get demandUnit() {
    if (this.isGsh) {
      return '(GJ)';
    } else {
      return '(TJ)';
    }
  }

  get isRegionDisabled() {
    return this.disabledRegions.includes(this.selectedRegion);
  }

  created() {
    this.updateDisabledRegions();
    this.fetchData();
    this.intervalRefresh = setInterval(() => {
      this.fetchData();
    }, 10000);
  }

  updateDisabledRegions() {
    if (this.$route.query.Gas_location) {
      const regions = (this.$route.query.Gas_location as string).split(',');
      this.disabledRegions = regions;
    }
  }

  applyFilter() {
    this.dynamicView.removeFilters();
    const isSttm = this.isSttm;
    const isDwgm = this.isDwgm;
    const isGsh = this.isGsh;

    if (this.isSttm) {
      this.dynamicView.applyFind({ 'HUB_DESCRIPTION': this.selectedRegion });
    } else if (this.isGsh) {
      this.gshLocations = Array.from(new Set(this.dynamicView.data().map(item => item.PRODUCT_LOCATION)));
      if (!this.selectedGshLocation && this.gshLocations.length > 0) this.selectedGshLocation = this.gshLocations[0];
      this.dynamicView.applyFind({ 'PRODUCT_LOCATION': this.selectedGshLocation });
    }
    
    this.data = this.dynamicView.data();
    let currentInterval = null;
    let nextInterval = null;

    for (let i = this.data.length - 1; i >= 0; i--) {
      const row = this.data[i];

      if (isSttm) {
        row.EX_ANTE_PRICE = parseFloat(row.EX_ANTE_PRICE);
        row.EX_ANTE_TJ = parseFloat(row.EX_ANTE_TJ);
        row.EX_POST_PRICE = parseFloat(row.EX_POST_PRICE);
        row.EX_POST_TJ = parseFloat(row.EX_POST_TJ);
      } else if (isDwgm) {
        row.PRICE = parseFloat(row.PRICE);
        row.DEMAND = parseFloat(row.DEMAND);
      } else if (isGsh) {
        if (!currentInterval) {
          currentInterval = row;
          currentInterval.PRICE = row.VOLUME_WEIGHTED_PRICE;
          currentInterval.DEMAND = row.TOTAL_QUANTITY;
        }
      }

      if (row.PERIODTYPE === 'ACTUAL') {
        if (!currentInterval) {
          currentInterval = row;
          row.dashLength = 5;
          if (!nextInterval && (i + 1) < this.data.length) {
            nextInterval = this.data[i+1];
          }

          if (isSttm) {
            if (nextInterval) {
              currentInterval = nextInterval;
            }
            currentInterval.PRICE = row.EX_POST_PRICE;
            currentInterval.DEMAND = row.EX_POST_TJ;
          } else if (isDwgm) {

          }
        }
      }
    }     
  
    this.currentInterval = currentInterval;
    this.nextInterval = nextInterval;
  }

  async fetchData() {
    if (this.isSttm) {
      this.id = await DataManager.addEndPoint('../../data/STTM/WEB_STTM_PRICE_AND_DEMAND.csv',undefined, 600000,undefined,'csv','yyyy-MM-ddTHH:mm:ss',['GAS_DATE']);
    } else if (this.isDwgm) {
      this.id = await DataManager.addEndPoint('../../data/DWGM/WEB_DWGM_PRICE_AND_DEMAND.CSV',undefined, 600000,undefined,'csv','yyyy-MM-ddTHH:mm:ss',['DATETIME']);
    } else if (this.isGsh) {
      this.id = await DataManager.addEndPoint('../api/report/GSH_TRANSACTION_SUMMARY', {}, 600000, 'GSH_TRANSACTION_SUMMARY', undefined, 'yyyy-MM-ddTHH:mm:ss', ['CURRENTDATE']);
    }
    
    this.dynamicView = DataManager.getDynamicView(this.id, 'gassummary');
    this.applyFilter();
    this.updateChartConfig();
  }

  updateChartConfig() {
    if (this.isDwgm) {
      this.dwgmChartConfig = this.getDwgmChartConfig();
    } else if (this.isSttm) {
      this.sttmChartConfig = this.getSttmChartConfig();
    } else if (this.isGsh) {
      this.gshChartConfig = this.getGshChartConfig();
    }
  }

  chartRendered(chart: any) {
    this.addChartLabels(chart);
    chart.addListener('drawn', (e: any) => {
      e.chart.clearLabels();
      this.addChartLabels(e.chart);
    });
  }

  addChartLabels(chart: any) {
    const yTitle = 26,
          yLabel = '!60',
          yValue = '!46',
          titleSize = 10,
          labelSize = 8,
          valueSize = 12,
          titleOpacity = 0.6,
          labelOpacity = 1,
          valueOpacity = 1;


    if (chart.categoryAxis.dateToCoordinate) {
      if (this.isDwgm && this.currentInterval.DATETIME) {
        let xCoordinate = chart.categoryAxis.dateToCoordinate(this.currentInterval.DATETIME);
        if (xCoordinate) xCoordinate += 30;
        if (isNaN(xCoordinate)) return;
        const value = formatDate(this.currentInterval.DATETIME, "HH:mm-") + formatDate(this.nextInterval.DATETIME, "HH:mm dd MMM");
        chart.addLabel(xCoordinate, yTitle, 'FORECAST', 'left', titleSize, this.guideColour, 0, titleOpacity, true);
        chart.addLabel(xCoordinate, yLabel, 'CURRENT INTERVAL', 'left', labelSize, this.guideColour, 0, labelOpacity, true);
        chart.addLabel(xCoordinate, yValue, value, 'left', valueSize, this.guideColour, 0, valueOpacity, true);
      } else if (this.isSttm && this.currentInterval.GAS_DATE) {
        let xCoordinate = chart.categoryAxis.dateToCoordinate(this.currentInterval.GAS_DATE);
        if (xCoordinate) xCoordinate += 30;
        if (isNaN(xCoordinate)) return;
        const value = formatDate(this.currentInterval.GAS_DATE, "dd MMM");
        chart.addLabel(xCoordinate, yLabel, 'TODAY', 'left', labelSize, this.guideColour, 0, labelOpacity, true);
        chart.addLabel(xCoordinate, yValue, value, 'left', valueSize, this.guideColour, 0, valueOpacity, true);
      } else if (this.isGsh && this.currentInterval.CURRENTDATE) {
        let xCoordinate = chart.categoryAxis.dateToCoordinate(this.currentInterval.CURRENTDATE);
        if (xCoordinate) xCoordinate += 30;
        if (isNaN(xCoordinate)) return;
        const value = formatDate(this.currentInterval.CURRENTDATE, "dd MMM");
        chart.addLabel(xCoordinate, yLabel, 'LATEST', 'left', labelSize, this.guideColour, 0, labelOpacity, true);
        chart.addLabel(xCoordinate, yValue, value, 'left', valueSize, this.guideColour, 0, valueOpacity, true);
      }
    }
  }

  @Watch('selectedRegion')
  @Watch('selectedGshLocation')
  onFilterChange(newValue: string, oldValue: string) {
    this.fetchData();
  }

  getSttmChartConfig() {
    return {
      type: 'serial',
      marginRight: 0,
      marginLeft: 0,
      marginTop: 1,
      marginBottom: 0,
      addClassNames: true,
      panEventsEnabled: false,
      numberFormatter: {
        precision: 2, 
        decimalSeparator: '.', 
        thousandsSeparator: ','
      },
      valueAxes: [{
        id: "v1",
        position: "right",
        labelsEnabled: false,
        gridAlpha: 0,
        axisAlpha: 0
      }, {
          id: "v2",
          position: "right",
          labelsEnabled: false,
          gridAlpha: 0,
          axisAlpha: 0
      }],
      graphs: [ 
        { 
          valueAxis: "v2",
          type: 'column',
          columnWidth: 0.5,

          balloonText: "EXPOST QTY: [[value]] TJ", 
          valueField: 'EX_POST_TJ', 
          
          lineThickness: 2, 
          fillAlphas: 1,
          fillColors: ['#34B9B3'],
          lineColor: '#34B9B3',
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000',
            showBullet:false
          },
          bulletSize: 1, 
          bulletBorderColor: '#ffffff',
          bulletBorderThickness: 6
        },
        { 
          valueAxis: "v2", 
          type: 'column',
          columnWidth: 0.5,
          balloonText: "EXANTE QTY: [[value]] TJ", 
          valueField: 'EX_ANTE_TJ', 
          lineThickness: 2, 
          fillAlphas: 0.2, 
          fillColors: ['#34B9B3'],
          lineColor: '#34B9B3',
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000',
            showBullet:false
          }
        },
        { 
          valueAxis: "v1", 
          type: 'smoothedLine',
          dashLength: 5,
          balloonText: "EXANTE PRICE: $[[value]]", 
          valueField: 'EX_ANTE_PRICE', 
          lineThickness: 3, 
          lineColor: '#B056BC',
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000',
            showBullet: true
          },
          bullet: 'round',
          bulletSize: 10
        },
        { 
          valueAxis: "v1", 
          type: 'smoothedLine',
          balloonText: "EXPOST PRICE: $[[value]]",
          valueField: 'EX_POST_PRICE',  
          lineThickness: 3, 
          lineColor: '#B056BC',
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000',
            showBullet:false
          },
          bullet: 'round',
          bulletSize: 10
        }
      ],
      guides: [{
        date: this.currentInterval.GAS_DATE,
        color: this.guideColour,
        lineColor: "#777da7",
        lineAlpha: 1,
        fillAlpha: 0.1,
        dashLength: 3,
        lineThickness: 2,
        inside: false,
        above: true,
      }],
      categoryAxis: {
        parseDates: true,
        //labelRotation: 45,
        minPeriod: "DD",
        color: '#ffffff',
        gridAlpha: 0,
        axisAlpha: 0,
        labelsEnabled: true
      },
      categoryField: "GAS_DATE",
      chartCursor: {
        zoomable: false,
        bulletsEnabled: true,
        bulletSize: 12,
        cursorColor: '#000000',
        categoryBalloonFunction: function(value) {
          let valueIndex = this.xToIndex(this.dateToCoordinate(value));
          value = this.data[valueIndex];
          let actual = value.dataContext.PERIODTYPE === 'ACTUAL';
          let label = formatDate(value.dataContext.GAS_DATE, "E do MMM");
          label += actual ? ' Actual' : ' Forecast';
          
          return label;
        }
      }    
    };
  }

  getGshChartConfig() {
    return {
      type: 'serial',
      marginRight: 0,
      marginLeft: 0,
      marginTop: 1,
      marginBottom: 0,
      addClassNames: true,
      panEventsEnabled: false,
      numberFormatter: {
        precision: 2, 
        decimalSeparator: '.', 
        thousandsSeparator: ','
      },
      valueAxes: [{
        id: "v1",
        position: "right",
        labelsEnabled: false,
        gridAlpha: 0,
        axisAlpha: 0
      }, {
        id: "v2",
        position: "right",
        labelsEnabled: false,
        gridAlpha: 0,
        axisAlpha: 0
      }],
      graphs: [ 
        { 
          valueAxis: "v2",
          type: 'column',
          columnWidth: 0.5,
          balloonText: "QUANTITY: [[value]] GJ", 
          valueField: 'TOTAL_QUANTITY',
          lineThickness: 2, 
          fillAlphas: 1,
          fillColors: ['#34B9B3'],
          lineColor: '#34B9B3',
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000',
            showBullet:false
          },
          bulletSize: 1, 
          bulletBorderColor: '#ffffff',
          bulletBorderThickness: 6
        },
        { 
          valueAxis: "v1", 
          type: 'smoothedLine',

          balloonText: "VOL WEIGHTED PRICE: $[[value]]",
          valueField: 'VOLUME_WEIGHTED_PRICE',  
          lineThickness: 3, 
          lineColor: '#B056BC',
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000',
            showBullet: true
          },
          bullet: 'round',
          bulletSize: 10
        }
      ],
      guides: [{
        date: this.currentInterval.CURRENTDATE,
        color: this.guideColour,
        lineColor: "#777da7",
        lineAlpha: 1,
        fillAlpha: 0.1,
        dashLength: 3,
        lineThickness: 2,
        inside: false,
        above: true,
      }],
      categoryAxis: {
        parseDates: true,
        //labelRotation: 45,
        minPeriod: "DD",
        color: '#ffffff',
        gridAlpha: 0,
        axisAlpha: 0,
        labelsEnabled: true
      },
      categoryField: "CURRENTDATE",
      chartCursor: {
        zoomable: false,
        bulletsEnabled: true,
        bulletSize: 12,
        cursorColor: '#000000',
        categoryBalloonFunction: function(value) {
          let valueIndex = this.xToIndex(this.dateToCoordinate(value));
          value = this.data[valueIndex];
          let label = formatDate(value.dataContext.CURRENTDATE, "E do MMM");
          return label;
        }
      }    
    };
  }

  getDwgmChartConfig() {
    return {
      type: 'serial',
      marginRight: 0,
      marginLeft: 1,
      marginTop: 1,
      marginBottom: 0,
      addClassNames: true,
      panEventsEnabled: false,
      numberFormatter: {
        precision: 2, 
        decimalSeparator: '.', 
        thousandsSeparator: ','
      },
      valueAxes: [{
        id: "v1",
        position: "right",
        labelsEnabled: false,
        gridAlpha: 0,
        axisAlpha: 0
      }, {
        id: "v2",
        position: "right",
        labelsEnabled: false,
        gridAlpha: 0,
        axisAlpha: 0
      }],
      graphs: [ 
        { 
          valueAxis: "v1", 
          // type: 'smoothedLine',
          balloonText: "", 
          bulletSize: 0, 
          lineThickness: 0, 
          valueField: 'DEMAND', 
          fillAlphas: 0.3,
          fillColors: ['#34B9B3', '#ffffff']
        },
        { 
          valueAxis: "v1", 
          // type: 'smoothedLine',
          balloonText: "DEMAND: [[value]] TJ", 
          bulletSize: 25, 
          bulletOffset: 10, 
          lineThickness: 5, 
          valueField: 'DEMAND', 
          lineColor: '#34B9B3', 
          dashLengthField: "dashLength",
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000',
            showBullet:false
          }
        },
        { 
          valueAxis: "v2", 
          balloonText: "PRICE: $[[value]] /GJ", 
          bulletSize: 25, 
          lineThickness: 3, 
          valueField: 'PRICE', 
          lineColor: '#B056BC',
          dashLengthField: "dashLength",
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000',
            showBullet:false
          }
        }
      ],
      categoryAxis: {
        parseDates: true,
        //labelRotation: 45,
        minPeriod: "mm",
        color: '#ffffff',
        gridAlpha: 0,
        labelsEnabled: true,
        axisAlpha: 0
      },
      categoryField: "DATETIME",
      guides: [{
        date: this.currentInterval.DATETIME,
        color: this.guideColour,
        lineColor: "#777da7",
        lineAlpha: 1,
        fillAlpha: 1,
        dashLength: 3,
        lineThickness: 2,
        inside: false,
        labelRotation: 0,
        above: true,
        boldLabel: true
      }],
      chartCursor: {
        zoomable: false,
        bulletsEnabled: true,
        bulletSize: 12,
        cursorColor: '#000000',
        categoryBalloonFunction: function(value) {
          var valueIndex = this.xToIndex(this.dateToCoordinate(value)),
              value = this.data[valueIndex],
              actual = value.dataContext.PERIODTYPE === 'ACTUAL',
              label = '';
          
          if (valueIndex == this.data.length - 1 ) {
            label = formatDate(value.dataContext.DATETIME, "HH:mm dd MMM");
          } else {
            label = formatDate(value.dataContext.DATETIME, "HH:mm-") + 
            formatDate(this.data[valueIndex+1].dataContext.DATETIME, "HH:mm dd MMM");
          } 
          
          label += actual ? ' Actual' : ' Forecast';
          
          return label;
        }
      }
    };
  }

  destroyed() {
    clearInterval(this.intervalRefresh)
  }
}



// WEBPACK FOOTER //
// src/views/gas/gas-dashboard.ts